import { S25ItemI } from "../../pojo/S25ItemI";
import { Proto } from "../../pojo/Proto";
import { LooseAutocomplete } from "../../pojo/Util";
import { EventSummary } from "./s25.event.summary.service";

export namespace SwarmSchedule {
    import ISODateString = Proto.ISODateString;
    import NumericalString = Proto.NumericalString;
    import PercentageString = Proto.PercentageString;
    import ISOTimeString = Proto.ISOTimeString;
    import EpochTimestamp = Proto.EpochTimestamp;
    import NumericalBoolean = Proto.NumericalBoolean;

    export interface BoardRespI {
        board_name: string;
        shared: 0 | 1;
        board_id: number;
        last_mod_user?: string;
        sharedUsers?: { username: string }[];
        last_timestamp?: string;
        event?: EventWSI[];
        event_query_name: string;
        location_query_name: string;
        last_prepared: ISODateString;
        mpg_pushback: NumericalBoolean;
    }

    export interface BoardI {
        sharedUsers: { username: string }[];
        shared: boolean;
        owner: string;
        boardId: number;
        boardName: string;
        boardUUID: string;
        username: string;
        sessionId: string;
        isOwner: boolean;
        eventQueryName: string;
        locationQueryName: string;
        lastPrepared: ISODateString;
        mpgPushback: boolean;
    }

    export interface EventWSI {
        room_id: number;
        start_time: string; //eg "13:45:00"
        profile_code: string; //eg W1 TU TH 20181206T235959
        end_time: string;
        uuid: string; //130780-176112-TR-133100-150000-15-burnside_195
        dow: string; //TR
        ts: number; //1628190517046 timestamp???
        item_timestamp?: number;
    }

    export interface ItemToI {
        ItemTO: EventI;
    }

    export interface EventI {
        start: string; //eg "13:45:00"
        end: string;
        profileCode: string; //eg W1 TU TH 20181206T235959
        itemUUID: string; //130780-176112-TR-133100-150000-15-burnside_195
        sourceRoomId: number;
        roomId: number;
        dow: string; //TR
        timestamp: number; //1628190517046 -- no clue what this does
        rowCol: {
            colUUID: string;
            rowUUID: string;
        };
    }

    export type OptimizerFile =
        | "events"
        | "spaces"
        | "organizations"
        | "import_results"
        | "partitions"
        | "features"
        | "journal"
        | "export_results"
        | "control"
        | "results";

    export type Dow = "M" | "T" | "W" | "R" | "F" | "S" | "U";
    export type DayPatterns = keyof typeof DayPatterns;
    export const DayPatterns = {
        MWF: 0,
        TR: 1,
        MW: 2,
        WF: 3,
        MF: 4,
        M: 5,
        W: 6,
        F: 7,
        T: 8,
        R: 9,
        S: 10,
        U: 11,
        MT: 12,
        TW: 13,
        WR: 14,
        RF: 15,
        FS: 16,
        SU: 17,
        MTW: 18,
        TWR: 19,
        WRF: 20,
        RFS: 21,
        FU: 22,
        MTWR: 23,
        TWRF: 24,
        WRFS: 25,
        MTWRF: 26,
        MTRF: 27,
        MTWF: 28,
        MWRF: 29,
        MR: 30,
        MTR: 31,
        MWR: 32,
        TF: 33,
        TRF: 34,
        TWF: 35,
    };

    export interface LinkedItemI {
        eventId: number;
        profileId: number;
    }

    export interface OccurrenceI {
        reservation_start_dt: Date;
        reservation_end_dt: Date;
    }

    export interface HeaderI {
        colHash: string;
        colIndex: number;
        dow: DayPatterns;
        header_text: string;
        isHidden: boolean;
        itemName: string;
        start: Date;
        daysOfWeek: Dow[];
    }

    export interface ModelI extends BoardI {
        // allDows:
        allowXDrag: boolean;
        displayRows: DayPatterns[];
        displayEnd: Date;
        displayEndHour: number;
        displayStart: Date;
        displayStartHour: number;
        headers: HeaderI[];
        items: ItemI[];
        rows: S25ItemI[];
        lastTimestamp: number;
        minFillRatio: number;
        origResults: any;
        origResultsMap: { [key: string]: ResultsMapI };
        placeBlocksByUUID: Function;
        timeFormat: string; //eg. "S25|h:mm a"
    }

    export interface ResultsMapI {
        key: string;
        name: string;
        outcome: "preassigned" | "placed" | "notplaced";
        space: number;
        type: "EVENT" | "MOVED";
        moved?: boolean | "true" | "false";
    }

    export interface ItemI {
        occs: OccurrenceI[];
        dow: DayPatterns;
        start: Date;
        startHour: number;
        startTime: string; //eg. 18:00:00
        end: Date;
        endHour: number;
        endTime: string;
        profileCode: string;
        daysOfWeek: Dow[];
    }

    export type OptimizerData = {
        events?: { event: OptimizerEvent[] };
        spaces?: OptimizerSpaces;
        results?: OptimizerResults;
        control?: OptimizerControl;
        features?: OptimizerFeatures;
        partitions?: OptimizerPartitions;
        organizations?: OptimizerOrganizations;
    };

    export type OptimizerEvent = {
        groupid?: NumericalString;
        key?: string;
        name?: string;
        type?: "group";
        enrollment?: NumericalString;
        organization?: NumericalString;
        title?: string;
        preferred?: { room: NumericalString };
        required?: { feature: string };
        occurrences?: {
            occurrence: {
                start: ISODateString;
                end: ISODateString;
            }[];
        };
        dates?: {
            // I have not seen this present in any data set
            start: ISODateString;
            end: ISODateString;
            day: {
                text: ISODateString;
            }[];
        };
        room: NumericalString;
        assigned?: "yes";
        // Only present if event has been altered in a way that changes the profile
        newpattern?: string;
    };

    export type OptimizerSpaces = {
        space: {
            capacity: NumericalString;
            id: NumericalString;
            name: string;
            partition: NumericalString;
            fill_ratio?: NumericalString;
            features: {
                feature: {
                    text: NumericalString;
                    // What the heck is this function???
                    "function () {↵ if (typeof Promise === 'function' && this instanceof Promise) {↵ return PROMISE_OBJECT_TO_STRING;↵ }↵ return originalObjectToString.call(this);↵ }": Function;
                }[];
            };
            blackout?: {
                dates: OptimizerBlackoutDate[];
            };
        }[];
    };

    export type OptimizerBlackoutDate = {
        start: ISODateString;
        end: ISODateString;
        pattern: {
            count: NumericalString;
            days: string; // Space-separated list of weekdays such as "Mon Tue Wed..."

            [key: string]: any; // Not sure what the complete type is
        };
    };

    export type OptimizerResults = {
        average_util: PercentageString;
        impossible: NumericalString;
        notplaced: NumericalString;
        overall_util: PercentageString;
        placed: NumericalString;
        result: OptimizerResult[];
    };

    export type OptimizerResult = {
        key: NumericalString;
        name: string;
        outcome: OptimizerPlacement;
        space?: NumericalString; // Only present if placed
        type: LooseAutocomplete<"EVENT">;
        moved?: boolean;
    };

    export type OptimizerControl = {
        default_enrollment: NumericalString;
        enrollment_adjuster: NumericalString;
        group_enrollment_function: LooseAutocomplete<"maxoverlap">;
        head_count: LooseAutocomplete<"expected">;
        minimum_fill_ratio: NumericalString;
        run_mode: LooseAutocomplete<"1">;
        time_padding: NumericalString;
        timezone_id: NumericalString;
    };

    export type OptimizerFeatures = {
        feature: {
            id: NumericalString;
            name: string;
        }[];
    };

    export type OptimizerPartitions = {
        partition: {
            id: NumericalString;
            name: string;
        }[];
    };

    export type OptimizerOrganizations = {
        organization: {
            id: NumericalString;
            name: string;
            partitions: {
                priority: NumericalString;
                partition: {
                    text: NumericalString;
                    // What the heck is this function???
                    "function () {↵ if (typeof Promise === 'function' && this instanceof Promise) {↵ return PROMISE_OBJECT_TO_STRING;↵ }↵ return originalObjectToString.call(this);↵ }": Function;
                }[];
            }[];
        }[];
    };

    export type OptimizerPlacement = "preassigned" | "notplaced" | "placed" | "impossible";

    export type BoardEvent = {
        origEvent: OptimizerEvent;
        eventName: string;
        boardUUID: string;
        eventId: NumericalString;
        moved: boolean;
        newpattern: string;
        profileId: NumericalString;
        syntheticProfile: boolean;
        profileCode: string;
        origDow: string;
        dow: string;
        origStartTime: ISOTimeString;
        startTime: ISOTimeString;
        endTime: ISOTimeString;
        occs: { reservation_start_dt: ISODateString; reservation_end_dt: ISODateString }[];
        room: BoardRoom;
        features: BoardFeature[];
        groupId: NumericalString;
        linkedItems: BoardEvent[];
        enrollment: number;
        organization: BoardOrganization;
        draggable: boolean;
        multipleRooms: boolean;
        roomId?: number;
        uuid?: string;
        startHour?: number;
        endHour?: number;
        blocks?: any;
        ts?: EventWSI["item_timestamp"];
    };

    export type BoardRoom = {
        roomId: number;
        roomName: SwarmSchedule.OptimizerSpaces["space"][0]["name"];
        maxCapacity?: number;
        partition?: BoardPartition;
        features?: BoardFeature[];
        blackoutDates?: SwarmSchedule.OptimizerBlackoutDate[];
    };

    export type BoardPartition = {
        partitionId: number;
        partitionName: string;
    };

    export type BoardFeature = {
        featureId: number;
        featureName: string;
    };

    export type BoardOrganization = {
        organizationId: number;
        organizationName: string;
        partitions: {
            priority: number;
            list: BoardPartition[];
        }[];
    };

    export type MessageRequestResponse = {
        ItemRequestContainer: {
            boardUUID: string;
            sessionId: string;
            username: string;
            messages: Message[];
        };
    };

    export type Message = DefaultMessage | UserMessage;

    export type DefaultMessage = {
        type: LooseAutocomplete<"default">;
        category: LooseAutocomplete<"default">;
        item: MessageItem;
    };

    export type UserMessage = {
        boardUUID: string;
        category: "user";
        type: "user-on" | "user-off";
        sessionId: "";
        username: string;
        item: MessageItem;
    };

    export type MessageItem = {
        itemUUID: string;
        dow: string;
        start: ISOTimeString;
        end: ISOTimeString;
        profileCode: string;
        sourceRoomId: number;
        roomId: number;
        timestamp: EpochTimestamp;
        rowCol: {
            rowUUID: NumericalString;
            colUUID: string;
        };
    };
}
